/**
 * Diversified product selection (M212, refreshed M217).
 *
 * The catalogue is imported in sequential blocks by category, so default
 * sort-by-date layouts end up showing consecutive pieces of the same
 * category / price tier / style. This module shuffles a list deterministically
 * (per call seed) while interleaving along three dimensions:
 *
 *   - category        (e.g. Anéis / Colares / Brincos)
 *   - price band      (entry / mid / premium — terciles of the current set)
 *   - style           (the multi-value tag list on each product)
 *
 * When the user has explicitly filtered on one of these dimensions
 * (e.g. picked "Colares"), pass it in `excludeDimensions` so the function
 * does NOT try to diversify on that axis — it would either be impossible
 * (only one value remains) or contrary to the user's intent.
 *
 * Seed strategy (changed in M217):
 *   - Callers pass `opts.seed` to get stable shuffles for the lifetime of
 *     a component mount (typical pattern: `useMemo(() => Date.now() ^
 *     Math.random()*1e9, [])`). Filter changes re-run diversify with the
 *     same seed → same order for the same subset.
 *   - Without `opts.seed`, every call generates a brand new random seed.
 *     This avoids the previous behavior of pinning to one tab-session seed
 *     and never re-shuffling, which made the catalogue feel static.
 */

// Mulberry32 — small, fast, statistically OK for shuffling.
function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function deriveSeed(opts) {
  if (opts && typeof opts.seed === "number" && opts.seed > 0) {
    return (opts.seed >>> 0) || 1;
  }
  // Fresh per-call random seed — every invocation reshuffles.
  return (Math.floor(Math.random() * 0xffffffff) >>> 0) || 1;
}

function seededShuffleInPlace(arr, rng) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function computePriceBands(products) {
  const prices = products.map((p) => Number(p.price) || 0).filter((x) => x > 0).sort((a, b) => a - b);
  if (prices.length === 0) return { lowMax: 0, midMax: 0 };
  const lowMax = prices[Math.floor(prices.length / 3)] || 0;
  const midMax = prices[Math.floor((prices.length * 2) / 3)] || 0;
  return { lowMax, midMax };
}

function priceBandOf(p, bands) {
  const x = Number(p.price) || 0;
  if (x <= bands.lowMax) return "entry";
  if (x <= bands.midMax) return "mid";
  return "premium";
}

/**
 * Returns a re-ordered copy of `products` that interleaves categories
 * (when allowed) and avoids repeating price bands / styles consecutively.
 *
 * @param {Array} products - input list (never mutated)
 * @param {Object} opts
 * @param {Array<'category'|'price'|'style'>} [opts.excludeDimensions=[]] dimensions to NOT diversify (typically because the user filtered on them)
 * @param {number} [opts.limit] optionally trim to N items after diversifying
 */
export function diversifyProducts(products, opts = {}) {
  const excludeDimensions = opts.excludeDimensions || [];
  if (!Array.isArray(products) || products.length <= 1) return products || [];

  const diversifyCategory = !excludeDimensions.includes("category");
  const diversifyPrice = !excludeDimensions.includes("price");
  const diversifyStyle = !excludeDimensions.includes("style");

  const rng = mulberry32(deriveSeed(opts));
  const bands = computePriceBands(products);

  // Bucket by category (or one global bucket if not diversifying by category).
  const buckets = new Map();
  for (const p of products) {
    const key = diversifyCategory ? (p.category || "_") : "_";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(p);
  }
  for (const arr of buckets.values()) seededShuffleInPlace(arr, rng);

  // Deterministic round-robin order over the category buckets.
  const cycleKeys = seededShuffleInPlace(Array.from(buckets.keys()), rng);

  const result = [];
  const recent = []; // last 3 picks — drives price/style penalty
  const RECENT_WINDOW = 3;
  let cycleIdx = 0;
  const totalIn = products.length;

  while (result.length < totalIn) {
    let pickedThisRound = false;
    for (let step = 0; step < cycleKeys.length; step++) {
      const k = cycleKeys[(cycleIdx + step) % cycleKeys.length];
      const bucket = buckets.get(k);
      if (!bucket || bucket.length === 0) continue;

      // Pick the item in this bucket that best avoids the recent picks.
      let bestIdx = 0;
      if ((diversifyPrice || diversifyStyle) && recent.length > 0) {
        let bestScore = -Infinity;
        for (let i = 0; i < bucket.length; i++) {
          const cand = bucket[i];
          let score = 0;
          for (let r = 0; r < recent.length; r++) {
            const weight = recent.length - r; // 1 = oldest, recent.length = newest-pen
            if (diversifyPrice && priceBandOf(cand, bands) === priceBandOf(recent[r], bands)) {
              score -= weight;
            }
            if (diversifyStyle) {
              const candStyles = cand.style || [];
              const recentStyles = recent[r].style || [];
              const overlap = candStyles.some((s) => recentStyles.includes(s));
              if (overlap) score -= weight * 0.5;
            }
          }
          if (score > bestScore) {
            bestScore = score;
            bestIdx = i;
          }
        }
      }
      const chosen = bucket.splice(bestIdx, 1)[0];
      result.push(chosen);
      recent.push(chosen);
      if (recent.length > RECENT_WINDOW) recent.shift();
      cycleIdx = (cycleIdx + step + 1) % cycleKeys.length;
      pickedThisRound = true;
      break;
    }
    if (!pickedThisRound) break; // safety net (shouldn't happen)
  }

  return typeof opts.limit === "number" ? result.slice(0, opts.limit) : result;
}
